import { ShieldCheck, Cpu, Eye, Lock } from "lucide-react";
import UrlAnalyzer from "@/components/UrlAnalyzer";

const FEATURES = [
  {
    Icon: Cpu,
    title: "Motor multimodal",
    desc: "Análisis frame a frame con Gemini 3 Flash. Detecta deepfakes, swaps faciales y video sintético.",
  },
  {
    Icon: Eye,
    title: "Veredicto explicable",
    desc: "No solo una etiqueta: recibes razonamiento, artefactos detectados y nivel de confianza.",
  },
  {
    Icon: ShieldCheck,
    title: "Soporte multi-plataforma",
    desc: "YouTube, TikTok, Instagram, Facebook y LinkedIn. Pega la URL, listo.",
  },
  {
    Icon: Lock,
    title: "Sin registro",
    desc: "Sin login, sin cookies. El video se procesa solo durante el análisis.",
  },
];

export default function HomePage() {
  return (
    <div className="min-h-screen vx-glow-bg text-white relative overflow-x-hidden">
      <div className="absolute inset-0 vx-dots-bg pointer-events-none opacity-60" aria-hidden />

      {/* Header */}
      <header className="relative z-10 border-b border-[--vx-border]/60 backdrop-blur-md bg-[--vx-bg]/60 sticky top-0">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
          <a href="/" className="flex items-center gap-2.5" data-testid="logo">
            <img
              src="/isotype.jpg"
              alt="IF Detector"
              width={40}
              height={40}
              className="w-10 h-10 rounded-lg object-cover ring-1 ring-white/10"
            />
            <div className="font-display font-bold tracking-tight text-lg leading-none">
              IF Detector
            </div>
          </a>
          <nav className="hidden sm:flex items-center gap-7 text-sm text-[--vx-text-dim]">
            <a href="#analizar" className="hover:text-white transition-colors">
              Analizar
            </a>
            <a href="#como-funciona" className="hover:text-white transition-colors">
              Cómo funciona
            </a>
          </nav>
          <a
            href="#analizar"
            className="text-sm font-semibold bg-white text-black px-4 py-2 rounded-lg hover:bg-white/90 transition-colors"
            data-testid="cta-header"
          >
            Probar ahora
          </a>
        </div>
      </header>

      {/* Hero */}
      <section
        id="analizar"
        className="relative z-10 max-w-4xl mx-auto px-5 sm:px-8 pt-16 sm:pt-24 lg:pt-32 pb-16"
      >
        <div className="text-center">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-[--vx-border-strong] bg-[--vx-surface] text-xs font-mono uppercase tracking-wider text-[--vx-text-dim] vx-fade-up">
            <span className="w-1.5 h-1.5 rounded-full bg-[--vx-real] vx-blink" />
            Detector forense en tiempo real
          </div>
          <h1
            className="font-display font-black text-5xl sm:text-6xl lg:text-7xl tracking-tighter leading-[0.95] mt-6 vx-fade-up vx-delay-1"
            data-testid="hero-title"
          >
            ¿Ese video es <span className="text-[--vx-fake]">IA</span>
            <br className="hidden sm:block" />
            <span className="sm:hidden"> </span>
            o es <span className="text-[--vx-real]">real</span>?
          </h1>
          <p className="text-base sm:text-lg lg:text-xl text-[--vx-text-dim] mt-6 max-w-2xl mx-auto leading-relaxed vx-fade-up vx-delay-2">
            Pega la URL de un video de YouTube, TikTok, Instagram, Facebook o LinkedIn.
            Te diremos en segundos si fue generado por IA, con porcentaje y evidencia.
          </p>
        </div>

        <div className="mt-12 vx-fade-up vx-delay-3">
          <UrlAnalyzer />
        </div>
      </section>

      {/* How it works */}
      <section
        id="como-funciona"
        className="relative z-10 max-w-6xl mx-auto px-5 sm:px-8 py-20 lg:py-28"
      >
        <div className="text-center max-w-2xl mx-auto mb-14">
          <div className="text-xs uppercase tracking-[0.2em] text-[--vx-text-faint] font-mono">
            Cómo funciona
          </div>
          <h2 className="font-display font-black text-3xl sm:text-4xl lg:text-5xl tracking-tighter mt-3">
            Un análisis forense
            <br />
            en menos de un minuto.
          </h2>
          <p className="text-base text-[--vx-text-dim] mt-5">
            Descargamos el video desde la URL, lo enviamos a nuestro modelo multimodal
            y devolvemos un informe en español con veredicto, confianza y artefactos detectados.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {FEATURES.map(({ Icon, title, desc }, idx) => (
            <div
              key={title}
              className="vx-card rounded-2xl p-6 sm:p-7 hover:border-[--vx-border-strong] transition-colors"
              data-testid={`feature-${idx}`}
            >
              <div className="w-10 h-10 rounded-xl bg-[--vx-surface-2] border border-[--vx-border] grid place-items-center mb-4">
                <Icon className="w-5 h-5 text-white" strokeWidth={1.8} />
              </div>
              <h3 className="font-display font-bold text-lg tracking-tight">{title}</h3>
              <p className="text-sm text-[--vx-text-dim] mt-2 leading-relaxed">{desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="relative z-10 max-w-4xl mx-auto px-5 sm:px-8 pb-20 lg:pb-28">
        <div className="vx-card rounded-3xl p-8 sm:p-12 text-center relative overflow-hidden">
          <div
            className="absolute inset-0 opacity-30 pointer-events-none"
            style={{
              background:
                "radial-gradient(ellipse 50% 70% at 50% 0%, rgba(255,255,255,0.12), transparent 60%)",
            }}
            aria-hidden
          />
          <h3 className="relative font-display font-black text-3xl sm:text-4xl tracking-tighter">
            ¿Tienes un video sospechoso?
          </h3>
          <p className="relative text-[--vx-text-dim] mt-3 max-w-xl mx-auto">
            Cópialo, pégalo, descúbrelo. Gratis, sin registro y en español.
          </p>
          <a
            href="#analizar"
            className="relative inline-flex items-center gap-2 mt-7 bg-white text-black font-semibold px-5 py-3 rounded-xl hover:bg-white/90 transition-colors"
            data-testid="cta-final"
          >
            Analizar un video
          </a>
        </div>
      </section>

      {/* Footer */}
      <footer className="relative z-10 border-t border-[--vx-border]/60 bg-[--vx-bg]">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 py-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 text-xs text-[--vx-text-faint]">
          <div className="flex items-center gap-2.5 flex-wrap">
            <img
              src="/isotype.jpg"
              alt="IF Detector"
              width={28}
              height={28}
              className="w-7 h-7 rounded-md object-cover ring-1 ring-white/10"
            />
            <span className="font-display font-semibold text-white">IF Detector</span>
            <span>·</span>
            <span>© {new Date().getFullYear()} · Análisis orientativo</span>
          </div>
          <div className="font-mono uppercase tracking-wider text-[--vx-text-dim]" data-testid="footer-attribution">
            Un servicio de Interactiva Films
          </div>
        </div>
      </footer>
    </div>
  );
}
